// CustomerUsersPage.jsx
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../util/spinner-shim'
import PropTypes from 'prop-types'

import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import withAppStore from '../../util/withAppStore'

import styles from '../../../css/home.module.css'
import stylesDevice from '../../../css/devicePage.module.css'

import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'

const getUserCustomers = () => {
  const { id: customerID } = AppStore.getCustomer()
  AppDispatcher.dispatch({ action: 'get-customer-users', customerID })
}

function CustomerUsersPage (props) {
  const navigate = useNavigate()
  const { customerID } = useParams()

  const [once, setOnce] = React.useState(false)

  const goBack = React.useCallback(() => {
    navigate('/settings')
  }, [navigate])

  const handleChange = React.useCallback(() => {
    // forceUpdate equivalent - component will re-render when AppStore changes
  }, [])

  React.useEffect(() => {
    window.scrollTo(0, 0)

    AppStore.addChangeListener(handleChange)
    const {
      data: {
        customer: {
          ready,
          data: {
            id: customerIDFromData,
          },
        },
      },
    } = props
    if (ready && customerIDFromData) {
      getUserCustomers()
    }

    return () => {
      AppStore.removeChangeListener(handleChange)
    }
  }, [handleChange, props.data?.customer?.ready, props.data?.customer?.data?.id])

  React.useEffect(() => {
    const {
      data: {
        customer: {
          data: {
            id: customerIDFromData,
          },
        },
        userCustomers = [],
        userCustomersReady = false,
      },
    } = props
    if (once === false && customerIDFromData && !userCustomersReady && userCustomers.length < 1) {
      setOnce(true)
      getUserCustomers()
    }
  }, [once, props.data?.customer?.data?.id, props.data?.userCustomers, props.data?.userCustomersReady])

  const {
    auth: {
      logout,
    },
    intl: {
      formatMessage,
    },
    pages: {
      CustomerUsersPage: title,
    },
  } = props

  const accountType = (level) => {
    if (level < 10) {
      return formatMessage({
        id: 'invite.accountType.viewer',
        defaultMessage: 'Viewer',
        description: 'viewer account type label',
      })
    }

    if (level < 20) {
      return formatMessage({
        id: 'invite.accountType.manager',
        defaultMessage: 'Manager',
        description: 'manager account type label',
      })
    }

    if (level < 30) {
      return formatMessage({
        id: 'invite.accountType.admin',
        defaultMessage: 'Administrator',
        description: 'administrator account type label',
      })
    }

    if (level < 40) {
      return formatMessage({
        id: 'invite.accountType.distributor',
        defaultMessage: 'Distributor',
        description: 'distributor account type label',
      })
    }

    return ''
  }

  const INVITE_USER = formatMessage({
    id: 'general.invite',
    defaultMessage: 'Invite User',
    description: 'Invite User button label',
  })

  const USERS = formatMessage({
    id: 'general.users',
    defaultMessage: 'Users',
    description: 'Users label',
  })

  const { userCustomers = [], userCustomersReady = false } = props.data

  let userList = []

  if (!userCustomersReady) {
    userList = [(
      <Spinner
        key='spinner'
        name='double-bounce'
        style={{ width: '30px', marginLeft: 'auto', marginRight: 'auto' }}
      />
    )]
  } else if (userCustomers.length < 1) {
    userList = []
  } else {
    userList = userCustomers
      .map((userCustomer) => {
        const {
          permissions: {
            level = 10,
          },
          user: {
            authentication_details,
            name,
            first_name: firstName,
            last_name: lastName,
            email,
          },
          user_id: userID,
        } = userCustomer

        const type = accountType(level)
        const fullName = firstName && lastName ? `${firstName} ${lastName}` : undefined
        const userName = fullName || name || email
        const onboarding = authentication_details ? authentication_details.onboarding : undefined

        return { onboarding, userID, userName, type }
      })
      .sort((a, b) => a.userName.localeCompare(b.userName))
      .map(x => (
        <Link
          to={`/${customerID}/settings/users/${x.userID}`}
          key={x.userID}
        >
          <section
            className={stylesDevice.settingButtonCustomer}
            style={{ display: 'inline-block', padding: '20px' }}
            key={x.userID}
          >
            <h4 style={{ overflow: 'hidden' }}>{x.userName}</h4>
            {x.type}
            {x.onboarding && (
              <FormattedMessage
                id='customer.users.onboarding'
                defaultMessage=' (Not Activated)'
              />
            )}
          </section>
        </Link>
      ))
  }

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={goBack}
        page={title}
        name={USERS}
        logOut={() => logout(navigate)}
      />
      <div className={styles.pageWidth}>
        <div className={styles.pageContainer}>
          <Link
            activeStyle={{ overflowWrap: 'break-word' }}
            style={{ display: 'inline-block' }}
            to='/user/invite'
            className={stylesDevice.settingButtonCustomer}
          >
            <section
              className={`${styles.userSection}`}
              key='user-invite'
              style={{ minHeight: '116px' }}
            >
              <div style={{ textAlign: 'center', margin: '23px 0px' }}>
                <i className='fa fa-plus' style={{ marginRight: '1em', fontSize: '20px' }} />
                {INVITE_USER}
              </div>
            </section>
          </Link>
          {userList}
        </div>
      </div>
      <Footer />
    </div>
  )
}

CustomerUsersPage.propTypes = {
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
}

export default withAppStore(injectIntl(CustomerUsersPage))
